import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useEntryFinder } from '~Components/EntryFinder';
import { useDispatch, useLogger, useSelector, usePaymentMethods, useI18n } from '~Hooks';
import * as actions from '~actions';

import { useGiftCardsContext } from './GiftCardsProvider';
import { SERVICE_TYPE } from '~../__queries__/globalTypes';
import { Box } from '~Styles';
import PaymentMethodSelector from './PaymentMethodSelector';
import PaymentStatus from '~Components/PaymentStatus';

type TransactionState =
  | { status: 'AWAITING_PAYMENT_METHOD' }
  | { status: 'AWAITING_PAYMENT' }
  | { status: 'SUCCESS' }
  | { status: 'ERROR'; error: string };

/*
  This component is responsible for handling the payment part of the gift card purchase.
  It will be responsible for:
  - Handling selection of payment method
  - Creating payment
  - Handling payment errors
    - Remove payment and order if payment fails
  - Callback to parent component with the result of the payment
*/

interface PaymentHandlerProps {
  onPaymentSuccess: () => void;
  onPaymentError: (message?: string, payload?: any) => void;
  amount: number;
}

const PaymentHandler = ({ onPaymentSuccess, onPaymentError, amount }: PaymentHandlerProps) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const findEntryByItemId = useEntryFinder();
  const paymentMethods = usePaymentMethods();
  const giftCardsContext = useGiftCardsContext();

  // If we need to select a payment method, set the state to AWAITING_PAYMENT_METHOD else AWAITING_PAYMENT
  const [transactionState, setTransactionState] = useState<TransactionState>({
    status: paymentMethods.all.length <= 1 ? 'AWAITING_PAYMENT' : 'AWAITING_PAYMENT_METHOD',
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | undefined>(
    paymentMethods.all.length <= 1 ? paymentMethods.getDefaultMethod() : undefined,
  );

  const orderId = useRef(uuid());
  const payment = useSelector((state) =>
    state.POSPayments.find((p) => p.order === orderId.current),
  );

  const createPayment = () => {
    if (!selectedPaymentMethod) {
      return;
    }

    const item = giftCardsContext.item;
    if (!item) {
      onPaymentError('Gift card item could not be found.');
      return;
    }

    const entry = findEntryByItemId(item.id);
    if (!entry) {
      onPaymentError('Gift card menu item not found');
      return;
    }

    dispatch(
      actions.initiateOrder({
        id: orderId.current,
        method: selectedPaymentMethod,
        serviceType: SERVICE_TYPE.EAT_HERE,
        orderNumber: 'GC',
      }),
    );

    dispatch(
      actions.addItemToOrder(orderId.current, {
        count: 1,
        description: '',
        entryId: entry.id,
        removedVariations: [],
        amount: amount,
        categoryId: item.categories[0].id,
        discount: 0,
        fraction: 1,
        itemId: item.id,
        title: i18n(item.titleLang),
        variations: [],
        vatRate: item.vatRate,
      }),
    );

    dispatch(
      actions.initiatePayment(orderId.current, {
        canCancel: false,
        method: selectedPaymentMethod,
        meta: {
          transactionId: uuid(),
        },
        amount: amount,
      }),
    );
  };

  useEffect(() => {
    if (transactionState.status === 'AWAITING_PAYMENT_METHOD') {
      return;
    }

    if (transactionState.status === 'AWAITING_PAYMENT') {
      if (!payment) {
        createPayment();
      }
      return;
    }
  }, [transactionState]);

  useEffect(() => {
    if (!payment || payment.status === 'PENDING') {
      return;
    }

    if (payment.status === 'COMPLETED') {
      onPaymentSuccess();
    }

    if (payment.status === 'FAILED') {
      // Remove POS order and payment
      dispatch({
        type: 'REMOVE_POS_PAYMENT',
        id: payment.id,
      });

      dispatch({
        type: 'REMOVE_POS_ORDER',
        id: orderId,
      });

      onPaymentError('Payment failed');
    }
  }, [payment?.status]);

  return (
    <div>
      {/* Step 0.5 - Select payment method if needed */}
      {transactionState.status === 'AWAITING_PAYMENT_METHOD' && (
        <Box fluid>
          <PaymentMethodSelector
            onPaymentMethodSelected={(method: string) => {
              setSelectedPaymentMethod(method);
              setTransactionState({ status: 'AWAITING_PAYMENT' });
            }}
          />
        </Box>
      )}
      {/* Step 1 - Show payment status */}
      {transactionState.status !== 'AWAITING_PAYMENT_METHOD' && (
        <div>
          {payment && (
            <PaymentStatus paymentId={payment.id} status={payment.status} textColor="colorBlack" />
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentHandler;
